import { useEffect, useRef, useState, useCallback } from 'react';

const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:5000';

export const useConsoleWS = (serverId) => {
  const ws = useRef(null);
  const [lines, setLines] = useState([]);
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState('disconnected'); // disconnected | connecting | connected | error
  const [serverState, setServerState] = useState(null); // running | offline | starting | stopping (from Pterodactyl)

  const connect = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    if (!token || !serverId) return;
    if (ws.current?.readyState === WebSocket.OPEN) return; // already connected

    setStatus('connecting');

    const socket = new WebSocket(`${WS_URL}/ws/console?token=${token}&serverId=${serverId}`);
    ws.current = socket;

    socket.onopen = () => {
      setConnected(true);
      setStatus('connected');
    };

    socket.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'console') {
          setLines((prev) => [...prev.slice(-500), msg.line]); // keep last 500 lines
        } else if (msg.type === 'connected') {
          setLines((prev) => [...prev, msg.message]);
        } else if (msg.type === 'status') {
          setServerState(msg.status);
          setLines((prev) => [...prev, `[Status] Server is now ${msg.status}`]);
        } else if (msg.type === 'error') {
          setLines((prev) => [...prev, `[Error] ${msg.message}`]);
          setStatus('error');
        }
      } catch {}
    };

    socket.onclose = () => {
      setConnected(false);
      setStatus((prev) => (prev === 'error' ? 'error' : 'disconnected'));
    };

    socket.onerror = () => {
      setStatus('error');
    };
  }, [serverId]);

  const disconnect = useCallback(() => {
    ws.current?.close();
    ws.current = null;
    setConnected(false);
    setStatus('disconnected');
  }, []);

  const sendCommand = useCallback((command) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ type: 'command', command }));
      setLines((prev) => [...prev, `> ${command}`]);
    }
  }, []);

  const clearLines = useCallback(() => setLines([]), []);

  useEffect(() => {
    return () => ws.current?.close();
  }, []);

  return { lines, connected, status, serverState, connect, disconnect, sendCommand, clearLines };
};
